import React, { FC, useContext, useState, useMemo } from 'react';
import styles from './index.module.scss';
import { ShareDataWrapperContext, DateContext } from '../../../index';
import { Button, Tooltip } from '@maxtropy/components';
import { Modal, Progress, Spin } from 'antd';
import { DoubleRightOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import dayjs, { Dayjs } from 'dayjs';
import { apiV2PaintingDetailDehumidifierPost, apiV2PaintingDetailVocsPost } from '@maxtropy/device-customer-apis-v2';
import { keepOneAndWanOrLine, keepTwoOrLine } from '@/utils/utils';
import { IndexTimeActive } from '../../SwitchDate';
import imgDevice from '../../../imgs/img_device.png';
import iconVocs from '../../../imgs/icon-VOCS.png';
import DehumidifierDetailModal from './DehumidifierDetailModal';
import VOCsDetailModal from './VOCsDetailModal';

const DeviceDetail: FC = () => {
  const { isStandardSize } = useContext(ShareDataWrapperContext);
  const { activeKey, selectedDate } = useContext(DateContext);
  const [dehumidifierModalVisible, setDehumidifierModalVisible] = useState(false);
  const [vocsModalVisible, setVocsModalVisible] = useState(false);

  const progressSize: [number, number] = isStandardSize ? [120, 8] : [80, 6];

  // 请求参数（用于卡片展示数据）
  const requestParams = useMemo(() => {
    const unit = activeKey === IndexTimeActive.THIS_MONTH ? 'month' : 'year';
    const startTime = selectedDate.startOf(unit).valueOf();
    const now = dayjs();
    const isCurrentPeriod =
      selectedDate.format(activeKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY') ===
      now.format(activeKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY');
    const endTime = isCurrentPeriod
      ? now.subtract(1, 'day').endOf('day').valueOf()
      : selectedDate.endOf(unit).valueOf();
    return { startTime, endTime };
  }, [activeKey, selectedDate]);

  // 调用除湿机接口（卡片展示）
  const { data: dehumidifierData, loading: dehumidifierLoading } = useRequest(
    () =>
      apiV2PaintingDetailDehumidifierPost(requestParams).then(res => ({
        summary: res?.summary,
      })),
    { refreshDeps: [requestParams] }
  );

  // 调用 VOCs 接口（卡片展示）
  const { data: vocsData, loading: vocsLoading } = useRequest(
    () =>
      apiV2PaintingDetailVocsPost(requestParams).then(res => ({
        summary: res?.summary,
      })),
    { refreshDeps: [requestParams] }
  );

  // 除湿机展示数据
  const dehumidifierDisplay = {
    name: '除湿机',
    count: dehumidifierData?.summary?.deviceCount ?? 0,
    electricityUsage: keepOneAndWanOrLine(dehumidifierData?.summary?.totalUsage),
    ratio: keepTwoOrLine(dehumidifierData?.summary?.ratio),
  };

  // VOCs 展示数据
  const vocsDisplay = {
    name: 'VOCS',
    count: vocsData?.summary?.deviceCount ?? 0,
    electricityUsage: keepOneAndWanOrLine(vocsData?.summary?.totalElecUsage),
    ratio: keepTwoOrLine(vocsData?.summary?.elecRatio),
  };

  return (
    <>
      <div className={styles.device_detail_cards}>
        {/* 左侧：除湿机 */}
        <div className={styles.device_detail_card}>
          {dehumidifierLoading ? (
            <div className={styles.loading_wrapper}>
              <Spin />
            </div>
          ) : (
            <>
              <div className={styles.device_left}>
                <img className={styles.device_image} src={imgDevice} alt="除湿机" />
                <div className={styles.device_name}>{dehumidifierDisplay.name}</div>
                <div className={styles.device_count}>{dehumidifierDisplay.count}台</div>
              </div>
              <div className={styles.device_right}>
                <div className={styles.device_header}>
                  <span className={styles.device_label}>{dehumidifierDisplay.name}用电量(kWh)</span>
                  <Button type="link" style={{ padding: 0 }} onClick={() => setDehumidifierModalVisible(true)}>
                    <DoubleRightOutlined />
                  </Button>
                </div>
                <Tooltip title={`${dehumidifierDisplay.electricityUsage}`}>
                  <div className={styles.device_value}>{dehumidifierDisplay.electricityUsage}</div>
                </Tooltip>
                <div className={styles.device_ratio}>占涂装总用电量</div>
                <div className={styles.device_progress_row}>
                  <Progress
                    percent={Number(dehumidifierDisplay.ratio) || 0}
                    showInfo={false}
                    strokeColor="#16DD8E"
                    trailColor="rgba(45, 141, 255, 0.2)"
                    size={progressSize}
                  />
                  <Tooltip title={`${dehumidifierDisplay.ratio}%`}>
                    <span className={styles.device_percentage}>{dehumidifierDisplay.ratio}%</span>
                  </Tooltip>
                </div>
              </div>
            </>
          )}
        </div>

        {/* 右侧：VOCs */}
        <div className={styles.device_detail_card}>
          {vocsLoading ? (
            <div className={styles.loading_wrapper}>
              <Spin />
            </div>
          ) : (
            <>
              <div className={styles.device_left}>
                <img className={styles.device_image} src={iconVocs} alt="VOCS" />
                <div className={styles.device_name}>{vocsDisplay.name}</div>
                <div className={styles.device_count}>{vocsDisplay.count}台</div>
              </div>
              <div className={styles.device_right}>
                <div className={styles.device_header}>
                  <span className={styles.device_label}>{vocsDisplay.name}用电量(kWh)</span>
                  <Button type="link" style={{ padding: 0 }} onClick={() => setVocsModalVisible(true)}>
                    <DoubleRightOutlined />
                  </Button>
                </div>
                <Tooltip title={`${vocsDisplay.electricityUsage}`}>
                  <div className={styles.device_value}>{vocsDisplay.electricityUsage}</div>
                </Tooltip>
                <div className={styles.device_ratio}>占涂装总用电量</div>
                <div className={styles.device_progress_row}>
                  <Progress
                    percent={Number(vocsDisplay.ratio) || 0}
                    showInfo={false}
                    strokeColor="#16DD8E"
                    trailColor="rgba(45, 141, 255, 0.2)"
                    size={progressSize}
                  />
                  <Tooltip title={`${vocsDisplay.ratio}%`}>
                    <span className={styles.device_percentage}>{vocsDisplay.ratio}%</span>
                  </Tooltip>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <Modal
        title={'除湿机用能'}
        open={dehumidifierModalVisible}
        onCancel={() => setDehumidifierModalVisible(false)}
        footer={null}
        width={1400}
        centered
        destroyOnClose
        className={styles.customModal}
        getContainer={() => document.getElementById('full_screen_box') ?? document.body}
      >
        <DehumidifierDetailModal
          visible={dehumidifierModalVisible}
          defaultActiveKey={activeKey}
          defaultSelectedDate={selectedDate}
        />
      </Modal>

      <Modal
        title={'VOCs 用能'}
        open={vocsModalVisible}
        onCancel={() => setVocsModalVisible(false)}
        footer={null}
        width={1400}
        centered
        destroyOnClose
        className={styles.customModal}
        getContainer={() => document.getElementById('full_screen_box') ?? document.body}
      >
        <VOCsDetailModal visible={vocsModalVisible} defaultActiveKey={activeKey} defaultSelectedDate={selectedDate} />
      </Modal>
    </>
  );
};

export default DeviceDetail;
